import PublicLayout from "@/components/layout/PublicLayout";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { checkoutStatus, api } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";
import { track } from "@/lib/analytics";
import PostCheckoutZipPrompt from "@/components/upload/PostCheckoutZipPrompt";

export default function CheckoutSuccess() {
    const [params] = useSearchParams();
    const session_id = params.get("session_id");
    const [status, setStatus] = useState("pending");
    const [data, setData] = useState(null);
    const [attempts, setAttempts] = useState(0);
    const [needsZip, setNeedsZip] = useState(false);
    const [zipDismissed, setZipDismissed] = useState(false);
    const [matchedReportId, setMatchedReportId] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        if (!session_id) {
            setStatus("error");
            return;
        }
        let cancelled = false;
        let timer;

        const poll = async (n) => {
            if (cancelled) return;
            try {
                const r = await checkoutStatus(session_id);
                setData(r);
                setAttempts(n);
                if (r.payment_status === "paid") {
                    setStatus("paid");
                    track("payment_completed", { session_id, product_id: r.product_id });
                    return;
                }
                if (r.status === "expired") {
                    setStatus("expired");
                    return;
                }
                if (n >= 8) {
                    setStatus("timeout");
                    return;
                }
                timer = setTimeout(() => poll(n + 1), 2200);
            } catch {
                if (n >= 8) {
                    setStatus("error");
                    return;
                }
                timer = setTimeout(() => poll(n + 1), 2500);
            }
        };
        poll(0);

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [session_id]);

    // After payment is confirmed, check whether the report tied to this
    // upload is missing a ZIP. If so, surface the inline ZIP prompt — this is
    // the highest-intent moment to capture localization for the report.
    useEffect(() => {
        if (status !== "paid" || !data?.upload_id) return;
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/dashboard/reports");
                const match = (r.data.reports || []).find(
                    (rr) => rr.upload_id === data.upload_id,
                );
                if (cancelled || !match) return;
                setMatchedReportId(match.report_id);
                if (!match.zip_code) setNeedsZip(true);
            } catch { /* non-critical */ }
        })();
        return () => {
            cancelled = true;
        };
    }, [status, data?.upload_id]);

    const proceed = async () => {
        // If purchase was tied to an upload, find its report and redirect there
        // so the user sees the generating state and we poll it for completion.
        if (data?.upload_id) {
            try {
                const r = await api.get("/dashboard/reports");
                const match = (r.data.reports || []).find(
                    (rr) => rr.upload_id === data.upload_id,
                );
                if (match) {
                    navigate(`/report/${match.report_id}`);
                    return;
                }
            } catch { /* non-critical */ }
        }
        navigate("/dashboard");
    };

    return (
        <PublicLayout>
            <section className="bl-paper">
                <div className="bl-container py-20 max-w-2xl" data-testid="checkout-success">
                    {status === "paid" && (
                        <>
                            <div className="w-14 h-14 rounded-full bg-bl-moss-tint flex items-center justify-center text-[var(--bl-moss)]">
                                <CheckCircle2 className="w-7 h-7" />
                            </div>
                            <h1 className="mt-6 font-display font-semibold text-3xl md:text-5xl tracking-tight text-bl">
                                Payment successful.
                            </h1>
                            <p className="mt-5 text-bl text-lg leading-relaxed">
                                Your access has been unlocked. Your full report is
                                being generated and saved to your dashboard. You will
                                never be asked to repurchase this report.
                            </p>
                            {needsZip && !zipDismissed && data?.upload_id && (
                                <PostCheckoutZipPrompt
                                    uploadId={data.upload_id}
                                    onApplied={() => {
                                        setNeedsZip(false);
                                        setZipDismissed(true);
                                        if (matchedReportId) {
                                            navigate(`/report/${matchedReportId}`);
                                        }
                                    }}
                                    onDismissed={() => setZipDismissed(true)}
                                />
                            )}
                            <button
                                onClick={proceed}
                                className="bl-btn-primary mt-7"
                                data-testid="checkout-continue-btn"
                            >
                                {needsZip && !zipDismissed
                                    ? "Skip & go to my dashboard"
                                    : "Go to my dashboard"}
                            </button>
                        </>
                    )}
                    {status === "pending" && (
                        <>
                            <div className="bl-spinner" />
                            <h1 className="mt-6 font-display font-semibold text-3xl tracking-tight text-bl">
                                Finalizing your purchase…
                            </h1>
                            <p className="mt-4 text-bl-muted">
                                This typically takes a few seconds. We'll redirect you
                                automatically.
                            </p>
                        </>
                    )}
                    {(status === "timeout" || status === "error" || status === "expired") && (
                        <>
                            <h1 className="font-display font-semibold text-3xl tracking-tight text-bl">
                                We're still verifying your payment.
                            </h1>
                            <p className="mt-4 text-bl-muted leading-relaxed">
                                Your payment may still complete. If you were charged,
                                your access will unlock automatically. If something
                                seems off, please{" "}
                                <a href="/contact" className="text-bl-primary">
                                    contact support
                                </a>{" "}
                                — we'll make it right.
                            </p>
                            <button
                                onClick={proceed}
                                className="bl-btn-secondary mt-6"
                                data-testid="checkout-timeout-dashboard-btn"
                            >
                                Continue to dashboard
                            </button>
                        </>
                    )}
                </div>
            </section>
        </PublicLayout>
    );
}
